import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Card, CardContent, Grid, IconButton, Skeleton, Stack, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import useMetricsDailyStats, { OnlyFansPayout } from '../../hooks/useMetricsDailyStats';
import useMetricsDailyStatsEarningsAll from '../../hooks/useMetricsDailyStatsEarningsAll';
import useSubscriberCategories from '../../hooks/useSubscriberCategories';
import useSubscriberStatsNewSubscribersCount from '../../hooks/useSubscriberStatsNewSubscribersCount';
import useTransactionsDailyStats, { OnlyFansTransactionsDailyStats } from '../../hooks/useTransactionsDailyStats';
import ReloginAlertCard from '../common/ReloginAlertCard';
import UsernameWithAvatar from '../common/UsernameWithAvatar';
import ValueAndTrend from '../subscriber/ValueAndTrend';
import DashboardMyAccountsServicesList from './DashboardMyAccountsServicesList';
import DashboardMyAccountsSharedCredit from './DashboardMyAccountsSharedCredit';

type DashboardMyAccountsSubscriberCardProps = {
    subscriber: any;
    setStats: (stats: any) => void;
    refetchSubscribers: () => void;
    openTransferCreditDialog: (subscriber: any) => void;
    amountType: 'gross' | 'net';
    todayDate: string;
    comparisonDate: string;
    /** Opens the dashboard comparison date dialog (same as table header calendar). */
    onOpenComparisonDateDialog: () => void;
    onUpdateDailyTransactionStats: (
        subscriberId: string,
        stats: { today: OnlyFansTransactionsDailyStats | null; comparison: OnlyFansTransactionsDailyStats | null },
    ) => void;
    onUpdateNewSubscribersToday: (subscriberId: string, count: number) => void;
    onUpdateSummaryLoadProgress: (
        subscriberId: string,
        progress: { earnings: boolean; today: boolean; comparison: boolean; newSubscribers: boolean },
    ) => void;
};

const DashboardMyAccountsSubscriberCard = React.memo((props: DashboardMyAccountsSubscriberCardProps) => {
    const {
        subscriber,
        setStats,
        refetchSubscribers,
        openTransferCreditDialog,
        amountType,
        todayDate,
        comparisonDate,
        onOpenComparisonDateDialog,
        onUpdateDailyTransactionStats,
        onUpdateNewSubscribersToday,
        onUpdateSummaryLoadProgress,
    } = props;
    const theme: Theme = useTheme();
    const timezone: string = useMemo(() => moment.tz.guess(), []);
    const startDate: Date = useMemo(() => moment().subtract(2, 'days').startOf('day').toDate(), []);
    const endDate: Date = useMemo(() => moment().endOf('day').toDate(), []);
    const { data: dailyStats, isFetching: dailyStatsLoading } = useMetricsDailyStats(startDate, endDate, timezone, subscriber._id);
    const { data: latestEarnings, isLoading: isLatestEarningsLoading } = useMetricsDailyStatsEarningsAll('latest', subscriber._id);
    const { data: latestEarningsMinusOneDay, isLoading: isLatestEarningsMinusOneDayLoading } = useMetricsDailyStatsEarningsAll(
        'oneBeforeLatest',
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
    );
    const { data: categories, isFetching: categoriesLoading } = useSubscriberCategories();

    const { data: todayDailyStats, isLoading: isTodayDailyStatsLoading } = useTransactionsDailyStats(
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
        todayDate,
        timezone,
    );
    const { data: comparisonDailyStats, isLoading: isComparisonDailyStatsLoading } = useTransactionsDailyStats(
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
        comparisonDate,
        timezone,
    );
    const gmtTodayStartDate = useMemo(() => moment.utc().startOf('day').toDate(), []);
    const gmtTodayEndDate = useMemo(() => moment.utc().toDate(), []);
    const { data: newSubscribersTodayCount, isLoading: isNewSubscribersTodayCountLoading } = useSubscriberStatsNewSubscribersCount(
        subscriber?._id && subscriber.isLoggedIn ? subscriber._id : null,
        gmtTodayStartDate,
        gmtTodayEndDate,
        'new',
    );

    const category = useMemo(() => {
        if (categoriesLoading || !subscriber.cognito || !subscriber.cognito.category) {
            return undefined;
        }

        return categories && categories.find(c => c._id === subscriber.cognito.category);
    }, [categories, categoriesLoading, subscriber.cognito]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        setStats((prevStats: Record<string, any>) => {
            const existing = Array.isArray(prevStats[subscriber._id]) ? prevStats[subscriber._id] : [{}, {}];
            const nextStats = [...existing];

            nextStats[0] = {
                ...(nextStats[0] || {}),
                ...(dailyStats?.[0] || {}),
                earnings: latestEarnings || undefined,
            };
            nextStats[1] = {
                ...(nextStats[1] || {}),
                ...(dailyStats?.[1] || {}),
                earnings: latestEarningsMinusOneDay || undefined,
            };

            return {
                ...prevStats,
                [subscriber._id]: nextStats,
            };
        });
    }, [dailyStats, latestEarnings, latestEarningsMinusOneDay, setStats, subscriber]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        onUpdateDailyTransactionStats(subscriber._id, {
            today: todayDailyStats || null,
            comparison: comparisonDailyStats || null,
        });
    }, [subscriber, todayDailyStats, comparisonDailyStats, onUpdateDailyTransactionStats]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        const numeric = Number(newSubscribersTodayCount ?? 0);
        onUpdateNewSubscribersToday(subscriber._id, Number.isFinite(numeric) ? numeric : 0);
    }, [subscriber, newSubscribersTodayCount, onUpdateNewSubscribersToday]);

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        onUpdateSummaryLoadProgress(subscriber._id, {
            earnings: !isLatestEarningsLoading && !isLatestEarningsMinusOneDayLoading,
            today: !isTodayDailyStatsLoading,
            comparison: !isComparisonDailyStatsLoading,
            newSubscribers: !isNewSubscribersTodayCountLoading,
        });
    }, [
        subscriber,
        isLatestEarningsLoading,
        isLatestEarningsMinusOneDayLoading,
        isTodayDailyStatsLoading,
        isComparisonDailyStatsLoading,
        isNewSubscribersTodayCountLoading,
        onUpdateSummaryLoadProgress,
    ]);

    const getValue = (earnings: OnlyFansPayout.PayoutsStats | null | undefined) => {
        if (!earnings) {
            return undefined;
        }

        if (amountType === 'gross') {
            return Number(earnings.list?.total?.all?.total_gross || 0);
        }

        return Number(earnings.list?.total?.all?.total_net || 0);
    };

    const getDailyAmount = (grossOrNetAmount: unknown): number => {
        const numeric = Number(grossOrNetAmount);

        return Number.isFinite(numeric) ? numeric : 0;
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                ...(category &&
                    category.color && {
                        backgroundImage: `linear-gradient(90deg, ${category.color} 0%, ${category.color} 4px, transparent 4px);`,
                    }),
            }}
        >
            <Box
                sx={{
                    p: 2,
                    pt: 1,
                }}
            >
                <UsernameWithAvatar subscriber={subscriber} category={category} refetchSubscribers={refetchSubscribers} />
            </Box>

            <CardContent sx={{ pt: 0, px: 2, pb: 2 }}>
                {!subscriber.isLoggedIn && (
                    <Box sx={{ mb: 1 }}>
                        <ReloginAlertCard subscriber={subscriber} refetchSubscriber={refetchSubscribers} />
                    </Box>
                )}

                {/* Fans | Following | Ranking */}
                <Stack direction="row" sx={{ mb: 1.5, py: 1 }}>
                    <Box sx={{ flex: 1, px: 0.5, borderRight: t => `1px solid ${t.palette.divider}` }}>
                        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 0.5 }}>
                            Fans
                        </Typography>
                        <ValueAndTrend
                            loading={dailyStatsLoading}
                            value={
                                dailyStats &&
                                dailyStats[0] &&
                                dailyStats[0].counters &&
                                dailyStats[0].counters.subscribers &&
                                dailyStats[0].counters.subscribers.active
                                    ? dailyStats[0].counters.subscribers.active
                                    : undefined
                            }
                            previousValue={
                                dailyStats &&
                                dailyStats[1] &&
                                dailyStats[1].counters &&
                                dailyStats.length > 1 &&
                                dailyStats[1].counters.subscribers &&
                                dailyStats[1].counters.subscribers.active
                                    ? dailyStats[1].counters.subscribers.active
                                    : undefined
                            }
                            type="number"
                            separateLines
                            textAlign="center"
                            fractionDigits={0}
                        />
                    </Box>
                    <Box sx={{ flex: 1, px: 0.5, borderRight: t => `1px solid ${t.palette.divider}` }}>
                        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 0.5 }}>
                            Following
                        </Typography>
                        <ValueAndTrend
                            loading={dailyStatsLoading}
                            value={
                                dailyStats &&
                                dailyStats[0] &&
                                dailyStats[0].counters &&
                                dailyStats[0].counters.subscriptions &&
                                dailyStats[0].counters.subscriptions.active
                                    ? dailyStats[0].counters.subscriptions.active
                                    : undefined
                            }
                            previousValue={
                                dailyStats &&
                                dailyStats.length > 1 &&
                                dailyStats[1].counters &&
                                dailyStats[1].counters.subscriptions &&
                                dailyStats[1].counters.subscriptions.active
                                    ? dailyStats[1].counters.subscriptions.active
                                    : undefined
                            }
                            type="number"
                            separateLines
                            textAlign="center"
                            fractionDigits={0}
                        />
                    </Box>
                    <Box sx={{ flex: 1, px: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 0.5 }}>
                            Ranking
                        </Typography>
                        <ValueAndTrend
                            loading={dailyStatsLoading}
                            value={dailyStats && dailyStats[0] && dailyStats[0].performerTop ? dailyStats[0].performerTop : undefined}
                            previousValue={
                                dailyStats && dailyStats.length > 1 && dailyStats[1].performerTop ? dailyStats[1].performerTop : undefined
                            }
                            type="number"
                            separateLines
                            textAlign="center"
                            logic="less"
                            fractionDigits={2}
                        />
                    </Box>
                </Stack>

                {/* Earnings | Daily Earnings */}
                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Earnings
                            </Typography>
                            <Tooltip title="Total earnings (latest vs prior snapshot)">
                                <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            </Tooltip>
                        </Stack>
                        <ValueAndTrend
                            loading={isLatestEarningsLoading || isLatestEarningsMinusOneDayLoading}
                            value={getValue(latestEarnings)}
                            previousValue={getValue(latestEarningsMinusOneDay)}
                            type="money"
                            textAlign="left"
                            fractionDigits={2}
                        />
                    </Grid>
                </Grid>

                {/* Daily Earnings */}
                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={12}>
                        {/* Title */}
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                Daily Earnings
                            </Typography>
                            <Tooltip title="Today vs selected comparison day (set on dashboard)">
                                <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            </Tooltip>
                        </Stack>

                        <Stack direction="row" sx={{ py: 1 }}>
                            {/* Today */}
                            <Box sx={{ flex: 1, px: 0.5, borderRight: t => `1px solid ${t.palette.divider}` }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    textAlign="center"
                                    sx={{ mb: 0.75, py: 0.6 }}
                                >
                                    Today
                                </Typography>
                                {isTodayDailyStatsLoading ? (
                                    <Skeleton variant="text" width="80%" sx={{ my: 0.5 }} />
                                ) : (
                                    <Typography
                                        variant="h6"
                                        color="primary.main"
                                        fontWeight={700}
                                        textAlign="center"
                                        sx={{ lineHeight: 1.2 }}
                                    >
                                        {`$${getDailyAmount(
                                            amountType === 'gross' ? todayDailyStats?.totalAmount : todayDailyStats?.totalNet,
                                        ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                    </Typography>
                                )}
                            </Box>

                            {/* Comparison */}
                            <Box sx={{ flex: 1, px: 0.5 }}>
                                <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mb: 0.75 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {moment(comparisonDate).format('ddd, MMM D')}
                                    </Typography>
                                    <Tooltip title="Select comparison date">
                                        <IconButton size="small" color="primary" onClick={onOpenComparisonDateDialog}>
                                            <CalendarMonthIcon fontSize="small" sx={{ fontSize: 18, color: 'primary.main' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                {isComparisonDailyStatsLoading ? (
                                    <Skeleton variant="text" width="80%" sx={{ mt: 0.5 }} />
                                ) : (
                                    <Typography
                                        variant="h6"
                                        color="text.secondary"
                                        fontWeight={700}
                                        textAlign="center"
                                        sx={{ lineHeight: 1.2 }}
                                    >
                                        {`$${getDailyAmount(
                                            amountType === 'gross' ? comparisonDailyStats?.totalAmount : comparisonDailyStats?.totalNet,
                                        ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}`}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                <Box sx={{ mb: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                            New Subscribers Today (GMT)
                        </Typography>
                        {isNewSubscribersTodayCountLoading ? (
                            <Skeleton variant="text" width={48} />
                        ) : (
                            <Typography variant="h6" color="primary.main">
                                {Number(newSubscribersTodayCount ?? 0).toLocaleString()}
                            </Typography>
                        )}
                    </Stack>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                    <DashboardMyAccountsSharedCredit
                        subscriber={subscriber}
                        openTransferCreditDialog={openTransferCreditDialog}
                        orientation="horizontal"
                    />
                </Box>

                <DashboardMyAccountsServicesList subscriber={subscriber} theme={theme} />
            </CardContent>
        </Card>
    );
});

export default DashboardMyAccountsSubscriberCard;
